import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate, useLocation } from "react-router-dom";
import { Trash2 } from "lucide-react";
import { SUBDOMAIN } from "../../../assets/meta/meta";
import { API, BACKEND_HOST } from "../../../api";
import { getAccessToken } from "../../../api/auth";
import { useAuthContext } from "../../../Components/AuthContext";
import CouponTicketSelectModal from "../../../Components/modal/CouponTicketSelectModal";
import { Modal, ModalBody } from "../../../Components/Common/Tailwind";
import { calcCouponSaleAmount } from "../../../common/utils/coupon_util";
import { usePGModules, useTossPaymentWidget, useOrderAgreements, dispatchPayment } from "../../../common/payments";
import { CollapsibleSection, OrdererInfoSection, RefundPolicySection, PGSelectorSection, PayButton } from "../components";
import { useTicketCartStore } from "../../../store/ticketCartStore";

const MultiOrderProcess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userInfo } = useAuthContext();
  const cartItems = useTicketCartStore((state) => state.items);
  const hydrateFromProducts = useTicketCartStore((state) => state.hydrateFromProducts);
  const removeCartItem = useTicketCartStore((state) => state.removeItem);

  const { pgModules, selectedPG, setSelectedPG } = usePGModules();
  const {
    cancelRuleAgree, purchaseOneAgree,
    setCancelRuleAgree, togglePurchaseAgreeOptions, allAgreed,
  } = useOrderAgreements();

  const [paying, setPaying] = useState(false);
  const [couponModal, setCouponModal] = useState(false);
  const [selectCoupon, setSelectCoupon] = useState([]);
  const [targetTicket, setTargetTicket] = useState([]);
  const [didHydrateFromLocation, setDidHydrateFromLocation] = useState(false);
  const [deleteTargetProductId, setDeleteTargetProductId] = useState(null);
  const checkoutProducts = useMemo(
    () => cartItems.filter((item) => item.quantity > 0),
    [cartItems]
  );
  const deleteTargetProduct = useMemo(
    () => checkoutProducts.find((item) => item.id === deleteTargetProductId),
    [checkoutProducts, deleteTargetProductId]
  );
  const fallbackPath = location.state?.type === 0 ? "/package" : "/ticket";

  useEffect(() => {
    if (checkoutProducts.length > 0) {
      return;
    }

    if (!didHydrateFromLocation && location.state?.products?.length) {
      hydrateFromProducts(location.state.products);
      setDidHydrateFromLocation(true);
      return;
    }

    navigate("/ticket", { replace: true });
  }, [checkoutProducts.length, didHydrateFromLocation, hydrateFromProducts, location.state, navigate]);

  const toggleTicketDiscountCouponSelection = (product_id, coupon_id, discount_amount, discount_type, min_purchase, discount_rate, max_discount = 0) => {
    setSelectCoupon((prev) => {
      let _prev = [...prev];
      const item = { coupon_id, product_id, discount_amount, discount_type, min_purchase, discount_rate, max_discount };
      const selected = _prev.find((i) => i.product_id === product_id && i.coupon_id === coupon_id);
      const blocked = _prev.find((i) => i.product_id !== product_id && i.coupon_id === coupon_id);
      if (blocked) return _prev;
      _prev = _prev.filter((i) => i.product_id !== product_id);
      return selected ? _prev : [..._prev, item];
    });
  };

  const orderSummary = useMemo(() => {
    let productAmount = 0;
    let discountAmount = 0;
    checkoutProducts.forEach((ticket) => {
      const _price = ticket.price * ticket.quantity;
      const _coupon = selectCoupon.find((c) => c.product_id === ticket.id);
      productAmount += _price;
      discountAmount += _coupon ? calcCouponSaleAmount(_coupon, _price) : 0;
    });
    return { productAmount, discountAmount, totalAmount: productAmount - discountAmount };
  }, [checkoutProducts, selectCoupon]);
  const totalOrderPrice = orderSummary.totalAmount;

  const { paymentWidgetRef } = useTossPaymentWidget({
    userInfo,
    totalOrderPrice,
    isTossSelected: selectedPG?.code === "toss",
  });

  const processPayment = async () => {
    const reqData = {
      tickets: checkoutProducts.map((item) => ({
        ...item,
        product_id: item.id,
        coupon_id: selectCoupon.find((c) => c.product_id === item.id)?.coupon_id || "",
      })),
    };

    const accessToken = getAccessToken();
    if (!accessToken) {
      toast.error("로그인이 필요한 서비스입니다.");
      navigate(`/login`);
      return;
    }
    if (totalOrderPrice === 0) {
      toast.error("결제가능한 최소금액은 1,000원입니다.");
      return;
    }

    setPaying(true);
    let shouldKeepPaying = false;
    try {
      const response = await API.post(`/ticket/pre-order-multi/`, JSON.stringify(reqData), {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const data = response.data;
      if ("error" in data) {
        toast.error(data.error);
        navigate(fallbackPath);
        return;
      }

      const paymentResult = await dispatchPayment({
        selectedPG,
        paymentWidgetRef,
        orderId: data.orderId,
        orderName: "티켓 종합",
        amount: totalOrderPrice,
        userInfo,
        urls: {
          successUrl: `${BACKEND_HOST}/ticket/order/`,
          failUrl: `https://${SUBDOMAIN}.${window.AMZ_ENV.VITE_DOMAIN}/ticket`,
          niceReturnUrl: "/ticket/order-nice/",
          hectoNotiUrl: `${BACKEND_HOST}/ticket/order-hecto/`,
          hectoNextUrl: `${BACKEND_HOST}/ticket/order-done-hecto/`,
          hectoCancUrl: `${BACKEND_HOST}/ticket/order-done-hecto/`,
        },
        failNavigate: () => navigate(fallbackPath),
      });
      shouldKeepPaying = paymentResult.redirectRequested;
    } catch (error) {
      console.log(error);
      if (error.response) {
        toast.error(`결제에 실패하였습니다. ${error.response.data?.error || String(error.response.data)}`);
      } else {
        toast.error("지금은 구매가능 시간이 아니거나 품절된 상품입니다.");
      }
      navigate(fallbackPath);
    } finally {
      if (!shouldKeepPaying) {
        setPaying(false);
      }
    }
  };

  const handleDeleteProduct = (productId) => {
    setSelectCoupon((prev) => prev.filter((coupon) => coupon.product_id !== productId));
    if (targetTicket?.id === productId) {
      setTargetTicket([]);
      setCouponModal(false);
    }
    removeCartItem(productId);
    setDeleteTargetProductId(null);
  };

  return (
    <>
        <div className="flex flex-col gap-3">
          <OrdererInfoSection userInfo={userInfo} />

          <CollapsibleSection title="주문상품" defaultOpen>
            <span className="font-gmarket text-sm font-semibold text-[#333]">
              티켓 종합 구매
              {!!checkoutProducts[0]?.site && <span className="ml-1 text-[#888]">({checkoutProducts[0].site})</span>}
            </span>

            <div className="mt-3 flex flex-col gap-3 px-2">
              {checkoutProducts.map((product) => {
                const couponSelected = selectCoupon.find((c) => c.product_id === product.id);
                if (!product.quantity) return null;
                const subtotal = product.price * product.quantity;
                const discountAmount = couponSelected ? calcCouponSaleAmount(couponSelected, subtotal) : 0;
                return (
                  <div key={product.id} className="rounded-xl border border-[#EEEEEE] bg-white overflow-hidden shadow-sm">
                    {/* 상단: 티켓 정보 */}
                    <div className="flex items-center gap-3 px-4 pt-4 pb-3">
                      {product.thumbnail && (
                        <img
                          src={product.thumbnail}
                          alt={product.name}
                          className="w-12 h-12 rounded-md object-cover shrink-0"
                        />
                      )}
                      <div className="flex-1 min-w-0 px-2">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-gmarket text-gray-900 text-sm font-bold leading-snug">{product.name}</span>
                          <button
                            className="shrink-0 flex items-center justify-center rounded-xl bg-gray-100 p-1.5 text-gray-600 hover:bg-red-400 transition-colors cursor-pointer"
                            aria-label={`${product.name} 주문 목록에서 삭제`}
                            onClick={() => setDeleteTargetProductId(product.id)}
                          >
                            <Trash2 size={15} strokeWidth={2} />
                          </button>
                        </div>
                        <span className="text-xs text-gray-900 mt-1 block">
                          {product.price.toLocaleString()}원 × {product.quantity}개
                        </span>
                      </div>
                    </div>

                    {/* 구분선 (안쪽 여백 적용) */}
                    <div className="mx-4 border-t border-[#EEEEEE]" />

                    {/* 하단: 합계 + 쿠폰 */}
                    <div className="flex items-center justify-between gap-3 px-4 py-3">
                      <div className="min-w-0">
                        {couponSelected ? (
                          <>
                            <span className="block text-xs text-gray-400 line-through">{subtotal.toLocaleString()}원</span>
                            <span className="block text-base font-bold text-[#e16036]">{(subtotal - discountAmount).toLocaleString()}원</span>
                          </>
                        ) : (
                          <span className="block text-base font-bold text-gray-900">{subtotal.toLocaleString()}원</span>
                        )}
                      </div>

                      <button
                        onClick={() => { setTargetTicket(product); setCouponModal(true); }}
                        className={[
                          "shrink-0 whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold border transition-colors cursor-pointer",
                          couponSelected
                            ? "bg-[#e16036] border-[#e16036] text-white"
                            : "bg-white border-[#e16036] text-[#e16036] hover:bg-[#FFF8F5]",
                        ].join(" ")}
                      >
                        {couponSelected ? "쿠폰 적용" : "쿠폰 선택"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </CollapsibleSection>

          {/* 결제금액 */}
          <CollapsibleSection title="결제금액" fixed>
            <div className="rounded-2xl bg-gray-50 px-4 py-4">
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between gap-4 text-gray-900">
                  <span>상품 금액</span>
                  <span className="font-semibold text-gray-900">{orderSummary.productAmount.toLocaleString()}원</span>
                </div>
                <div className="flex items-center justify-between gap-4 text-gray-900">
                  <span>할인 금액</span>
                  <span className={orderSummary.discountAmount > 0 ? "font-semibold text-[#e16036]" : "font-semibold text-gray-900"}>
                    {orderSummary.discountAmount > 0 ? "-" : ""}{orderSummary.discountAmount.toLocaleString()}원
                  </span>
                </div>
                <div className="mt-3 flex items-center justify-between gap-4 border-t border-gray-200 pt-3">
                  <span className="text-base font-bold text-gray-900">총결제금액</span>
                  <span className="text-lg font-bold text-[#e16036]">{totalOrderPrice.toLocaleString()}원</span>
                </div>
              </div>
            </div>
          </CollapsibleSection>

          {/* 환불 규정 */}
          <RefundPolicySection
            type="ticket"
            cancelRuleAgree={cancelRuleAgree}
            purchaseOneAgree={purchaseOneAgree}
            onCancelRuleChange={setCancelRuleAgree}
            onPurchaseAgreeChange={togglePurchaseAgreeOptions}
          />

          {/* PG 선택 */}
          <PGSelectorSection pgModules={pgModules} selectedPG={selectedPG} onSelect={setSelectedPG} />

          {/* 하단 고정 결제 버튼 여백 */}
          <div style={{ height: "100px" }} />
        </div>

        {/* 결제 버튼 */}
        <PayButton
          loading={paying}
          disabled={!allAgreed}
          onClick={() => {
            if (!allAgreed) { toast.warning("동의항목을 확인해주세요."); return; }
            processPayment();
          }}
          amount={totalOrderPrice}
        />

        <CouponTicketSelectModal
          setVisible={setCouponModal}
          visible={couponModal}
          ticket={targetTicket}
          selectCoupon={selectCoupon}
          setSelectCoupon={setSelectCoupon}
          toggleTicketDiscountCouponSelection={toggleTicketDiscountCouponSelection}
          navigate={navigate}
        />

        <Modal
          isOpen={deleteTargetProductId !== null}
          toggle={() => setDeleteTargetProductId(null)}
          centered
          size="sm"
        >
          <ModalBody className="px-6 pt-7 pb-6 text-center">
            <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100 text-gray-500">
              <Trash2 size={28} strokeWidth={1.4} />
            </div>
            {!!deleteTargetProduct?.name && (
              <h3 className="mb-3 text-base font-bold text-gray-900" style={{ fontFamily: "Gmarket-B" }}>
                {deleteTargetProduct.name}
              </h3>
            )}
            <p className="mb-1 text-sm font-bold text-gray-800 leading-relaxed">
              해당 티켓을 주문 목록에서 삭제하시겠습니까?
            </p>
           
            <div className="flex gap-2 mt-5">
              <button
                type="button"
                className="flex-1 h-11 rounded-full border border-gray-300 bg-white text-sm font-semibold text-gray-600 cursor-pointer"
                onClick={() => setDeleteTargetProductId(null)}
              >
                취소
              </button>
              <button
                type="button"
                className="flex-1 h-11 rounded-full border border-[#e16036] bg-white text-sm font-semibold text-[#e16036] cursor-pointer hover:bg-[#FFF8F5] transition-colors"
                onClick={() => {
                  if (deleteTargetProductId !== null) {
                    handleDeleteProduct(deleteTargetProductId);
                  }
                }}
              >
                삭제
              </button>
            </div>
          </ModalBody>
        </Modal>
    </>
  );
};

export default MultiOrderProcess;
